import { useEffect, useMemo, useState } from 'react';
import {
  createWarehouseLocation,
  deleteWarehouseLocation,
  fetchWarehouseLocationBranches,
  fetchWarehouseLocations,
  updateWarehouseLocation,
} from '../services/warehouseLocationService';

const INITIAL_VALUES = { name: '', branchId: '' };

function useWarehouseLocations() {
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [formValues, setFormValues] = useState(INITIAL_VALUES);
  const [formErrors, setFormErrors] = useState({});
  const [editingWarehouseLocation, setEditingWarehouseLocation] = useState(null);
  const [editValues, setEditValues] = useState(INITIAL_VALUES);
  const [editErrors, setEditErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadWarehouseLocations() {
      setIsLoading(true);
      setLoadError('');

      try {
        const [locationsResponse, branchesResponse] = await Promise.all([
          fetchWarehouseLocations(),
          fetchWarehouseLocationBranches(),
        ]);

        if (isMounted) {
          setWarehouseLocations(locationsResponse);
          setBranchOptions(
            branchesResponse.map((branch) => ({
              value: String(branch.id),
              label: branch.name,
              description: branch.status === 'inactive' ? 'Inactive' : '',
            }))
          );
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadWarehouseLocations();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  const sortedWarehouseLocations = useMemo(
    () =>
      [...warehouseLocations].sort((first, second) => first.name.localeCompare(second.name)),
    [warehouseLocations]
  );

  function handleFormChange(event) {
    const { name, value } = event.target;
    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setFormErrors({});
    setSubmitError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateWarehouseLocationForm(formValues, warehouseLocations);

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdWarehouseLocation = await createWarehouseLocation({
        name: formValues.name.trim(),
        branchId: Number(formValues.branchId),
      });

      setWarehouseLocations((currentLocations) => [
        ...currentLocations,
        createdWarehouseLocation,
      ]);
      setFormValues(INITIAL_VALUES);
      setSuccessMessage('Warehouse location added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function openEditModal(warehouseLocation) {
    setEditingWarehouseLocation(warehouseLocation);
    setEditValues({
      name: warehouseLocation.name,
      branchId: warehouseLocation.branchId ? String(warehouseLocation.branchId) : '',
    });
    setEditErrors({});
    setSubmitError('');
  }

  function closeEditModal() {
    setEditingWarehouseLocation(null);
    setEditValues(INITIAL_VALUES);
    setEditErrors({});
  }

  function handleEditChange(event) {
    const { name, value } = event.target;
    setEditValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setEditErrors({});
    setSubmitError('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();

    const nextErrors = validateWarehouseLocationForm(
      editValues,
      warehouseLocations,
      editingWarehouseLocation?.id
    );

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const updatedWarehouseLocation = await updateWarehouseLocation(
        editingWarehouseLocation.id,
        {
          name: editValues.name.trim(),
          branchId: Number(editValues.branchId),
        }
      );

      setWarehouseLocations((currentLocations) =>
        currentLocations.map((warehouseLocation) =>
          warehouseLocation.id === updatedWarehouseLocation.id
            ? updatedWarehouseLocation
            : warehouseLocation
        )
      );
      closeEditModal();
      setSuccessMessage('Warehouse location updated successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(warehouseLocation) {
    setIsSaving(true);
    setSubmitError('');

    try {
      await deleteWarehouseLocation(warehouseLocation.id);
      setWarehouseLocations((currentLocations) =>
        currentLocations.filter((currentLocation) => currentLocation.id !== warehouseLocation.id)
      );
      setSuccessMessage('Warehouse location deleted successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    warehouseLocations: sortedWarehouseLocations,
    branchOptions,
    formValues,
    formErrors,
    editingWarehouseLocation,
    editValues,
    editErrors,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    handleFormChange,
    handleSubmit,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
  };
}

function validateWarehouseLocationForm(
  values,
  warehouseLocations = [],
  currentWarehouseLocationId = null
) {
  const errors = {};
  const normalizedName = values.name.trim();
  const normalizedBranchId = String(values.branchId || '').trim();

  if (!normalizedName) {
    errors.name = 'Warehouse location is required.';
  }

  if (!normalizedBranchId) {
    errors.branchId = 'Branch is required.';
  }

  const hasDuplicateName = warehouseLocations.some((warehouseLocation) => {
    if (
      currentWarehouseLocationId !== null &&
      warehouseLocation.id === currentWarehouseLocationId
    ) {
      return false;
    }

    return (
      String(warehouseLocation.name || '').trim().toLowerCase() ===
      normalizedName.toLowerCase()
    );
  });

  if (hasDuplicateName) {
    errors.name = 'This warehouse location already exists.';
  }

  return errors;
}

export default useWarehouseLocations;
